<template>
  <div class="max-w-[720px] w-full px-4 sm:px-0 sm:mr-5">
    <h2 class="w-full text-center mb-8">
      {{ $t('messages.menu.entra') }}
    </h2>
    <div class="list--account flex flex-col border border-gray-200 rounded-lg sm:ml-10 bg-white">
      <div class="item--profile border-b border-gray-200 last:border-b-0 p-6">
        <div class="flex justify-between items-start">
          <h3 class="mb-3">
            {{ $t('messages.entra_setup.setup_label') }}
          </h3>
          <label
            class="relative items-center cursor-pointer"
            for="entraOption"
          >
            <input
              id="entraOption"
              v-model="entraOption"
              type="checkbox"
              class="sr-only peer"
              :disabled="primaryConfigState.isWorking"
              aria-label="enable label"
            >
            <div :class="toggleClasses" />
          </label>
        </div>
        <div class="b1">
          <p class="text-gray-500 mb-3">
            {{ $t('messages.entra_setup.description') }}
            <a
              class="inline-flex ml-2 text-zyxel-green-700 cursor-pointer"
              href="https://community.zyxel.com/en/discussion/29366"
              target="_blank"
              @click="goToUrl('https://community.zyxel.com/en/discussion/29366')"
            >
              {{ $t('messages.entra_setup.learn_more') }} <img
                class="h-5 ml-1"
                src="@/assets/svg/icon_20px_reopen.svg"
                alt="learn more icon"
              >
            </a>
          </p>
        </div>
      </div>
      <div
        v-if="entraOption"
        class="caption w-full p-6"
      >
        <dl class="grid sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 sm:gap-y-5 flex items-center">
          <dt class="text-zyxel-gray-900 max-w-[240px] sm:mr-[150px]">
            {{ $t('messages.entra_setup.domain_label') }}
          </dt>
          <dd :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}">
            {{ domain }}
          </dd>
          <dt class="text-zyxel-gray-900 max-w-[240px] mt-3 sm:mt-0 sm:mr-[150px]">
            {{ $t('messages.entra_setup.metadata_label') }}
          </dt>
          <dd>
            <button
              :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true, 'label': true, 'text-zyxel-green-700': true, 'font-bold': true, 'cursor-pointer': true}"
              @click="downloadMetadata()"
            >
              {{ $t('messages.entra_setup.download_metadata_label') }}
            </button>
          </dd>
          <dt
            v-if="primaryConfigState.isWorking"
            class="text-zyxel-gray-900 max-w-[240px] sm:mr-[150px]"
          >
            {{ $t('messages.entra_setup.status_label') }}
          </dt>
          <dd
            v-if="primaryConfigState.isWorking"
            :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}"
          >
            <div
              class="float-left"
              :class="stateColor[primaryConfigState.stateIndex]"
            />
            {{ firstEntraSetting.state }}
          </dd>
          <dt
            v-if="primaryConfigState.isWorking"
            class="text-zyxel-gray-900 max-w-[240px] mt-3 sm:mt-0 sm:mr-[150px]"
          >
            {{ $t('messages.entra_setup.app_id_label') }}
          </dt>
          <dd
            v-if="primaryConfigState.isWorking"
            :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}"
          >
            {{ firstEntraSetting.entity_id.match(/\/([^\/]+)\/$/)[1] }}
          </dd>
        </dl>
        <button
          v-if="primaryConfigState.isWorking&&!isSecondSetting"
          class="inline-flex mt-6 text-zyxel-green-700 cursor-pointer"
          @click="secondSetting()"
        >
          {{ $t('messages.entra_setup.upload_new_metadata_label') }}
        </button>
      </div>
      <div class="container mx-auto">
        <hr class="border-gray-200 w-11/12 mx-auto">
      </div>
      <div v-if="!isEditing&&entraOption&&entraSettings&&!primaryConfigState.isWorking">
        <div class="p-6">
          <div class="flex justify-between items-start">
            <h3 class="mb-5">
              {{ $t('messages.entra_setup.configuration_title') }}
            </h3>
            <div class="hidden sm:block md:block">
              <button
                class="cursor-pointer text-zyxel-green-700 font-bold ml-2"
                @click="edit"
              >
                {{ $t('messages.common.link_edit') }}
              </button>
            </div>
          </div>
          <div
            v-if="primaryConfigState.isUnverified&&isVerifySummited"
            class="bg-amber-50 p-4 mb-4 rounded-sm"
          >
            <div class="flex items-center">
              <p class="text-zyxel-gray-800">
                {{ $t('messages.entra_setup.refresh_instruction') }}
                <button
                  class="text-zyxel-green-600 hover:text-zyxel-green-800 font-medium underline focus:outline-none"
                  @click="handleRefresh"
                >
                  {{ $t('messages.entra_setup.reload_button') }}
                </button>
              </p>
            </div>
          </div>
          <dl class="grid sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 sm:gap-y-5 flex items-center">
            <dt class="text-zyxel-gray-900 max-w-[240px] sm:mr-[120px]">
              {{ $t('messages.entra_setup.status_label') }}
            </dt>
            <dd :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}">
              <div
                class="float-left"
                :class="stateColor[primaryConfigState.stateIndex]"
              />
              {{ firstEntraSetting.state }}
            </dd>
            <dt class="text-zyxel-gray-900 max-w-[240px] mt-3 sm:mt-0 sm:mr-[120px]">
              {{ $t('messages.entra_setup.app_id_label') }}
            </dt>
            <dd :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}">
              {{ primaryConfigState.entityId }}
            </dd>
            <dt
              v-if="primaryConfigState.isVerified"
              class="text-zyxel-gray-800 max-w-[240px] mt-3 sm:mt-0 sm:mr-[90px]"
            >
              <label for="primaryDeploymentCode">{{ $t('messages.entra_setup.deployment_code_label') }}</label>
            </dt>
            <dd
              v-if="primaryConfigState.isVerified"
              :class="{'text-ellipsis': true}"
            >
              <input
                id="primaryDeploymentCode"
                v-model="deployment_code"
                :class="['text-input p-4 text-left h-12 w-full']"
                type="text"
                aria-label="Deployment Code"
              >
              <div class="flex items-center gap-2 text-zyxel-gray-800">
                <span>{{ $t('messages.entra_setup.miss_deployment_code_label') }}</span>
                <button
                  :disabled="isDisabled"
                  class="transition-colors duration-200 focus:outline-none"
                  :class="isDisabled ? 'text-zyxel-gray-400 cursor-not-allowed' : 'text-zyxel-green-600 hover:text-zyxel-green-700'"
                  @click="handleRegenerate"
                >
                  {{ $t('messages.entra_setup.regenerate_label') }}{{ countdownText }}
                </button>
              </div>
            </dd>
          </dl>
          <div class="mt-5 modal-footer hidden sm:flex justify-end">
            <button
              v-if="primaryConfigState.isUnverified"
              class="mt-5 button-primary min-w-[120px]"
              @click="verify_configurtion(firstEntraSetting)"
            >
              {{ $t('messages.button.verify') }}
            </button>
            <button
              v-if="primaryConfigState.isVerified"
              class="mt-5 button-primary min-w-[120px]"
              @click="deploy_configurtion"
            >
              {{ $t('messages.button.deploy') }}
            </button>
          </div>
          <div class="modal-footer flex-col sm:hidden p-6">
            <button
              v-if="primaryConfigState.isUnverified"
              class="button-primary w-full mb-4"
              @click="verify_configurtion(firstEntraSetting)"
            >
              {{ $t('messages.button.verify') }}
            </button>
            <button
              v-if="primaryConfigState.isVerified"
              class="button-primary w-full mb-4"
              @click="deploy_configurtion"
            >
              {{ $t('messages.button.deploy') }}
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="entraOption&&isMetadataFileUpload"
        class="p-6"
      >
        <div class="flex justify-between items-start">
          <h3
            v-if="!isSecondSetting"
            class="mb-5"
          >
            {{ $t('messages.entra_setup.upload_metadata_title') }}
          </h3>
          <h3
            v-else
            class="mb-5"
          >
            {{ $t('messages.entra_setup.upload_metadata_title') }}
          </h3>
          <div class="hidden sm:block md:block">
            <div
              v-if="selectedFile"
              class="flex-row block"
            >
              <a
                class="text-zyxel-green-700 font-bold cursor-pointer"
                tabindex="0"
                role="button"
                @click="cancelEdit"
                @keydown.enter="cancelEdit"
                @keydown.space="cancelEdit"
              >
                {{ $t('messages.common.link_cancel') }}
              </a>
              <a
                class="cursor-not-allowed text-zyxel-gray-800 font-bold ml-2"
                tabindex="0"
                role="button"
                :class="{ 'cursor-pointer text-zyxel-green-700': selectedFile }"
                @click="handleFileUpload(selectedFile)"
                @keydown.enter="handleFileUpload(selectedFile)"
                @keydown.space="handleFileUpload(selectedFile)"
              >
                {{ $t('messages.common.link_save') }}
              </a>
            </div>
          </div>
        </div>

        <div
          v-if="!selectedFile"
          class="border-2 border-dotted border-green-500 rounded-lg p-10 text-center transition-colors duration-300"
          :class="{ 'bg-cyan-100': isDragging }"
          tabindex="0"
          role="button"
          @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
          @keydown.enter="triggerFileInput"
          @keydown.space="triggerFileInput"
        >
          <div class="flex flex-col items-center gap-3">
            <img
              class="h-10 w-10"
              src="@/assets/svg/icon_file_upload.svg"
              alt="upload icon"
            >
            <p class="m-0 text-base text-gray-700">
              {{ $t('messages.entra_setup.upload_instruction') }}
            </p>
            <span class="text-gray-700">or</span>

            <label
              for="fileInput"
              class="button-primary inline-block px-5 py-2 cursor-pointer"
            >
              {{ $t('messages.entra_setup.upload_button') }}
            </label>
            <!-- 隱藏的 input 為了製造檔案上傳效果 -->
            <input
              id="fileInput"
              ref="fileInput"
              type="file"
              accept=".xml"
              hidden
              @change="onFileChange"
            >
          </div>
        </div>
        <!-- 顯示已選擇的檔案名稱 -->
        <div
          v-if="selectedFile"
          class="border-2 border-green-500 rounded-lg p-4 flex justify-between items-center"
        >
          <div class="flex items-center space-x-2">
            <img
              class="ml-6 h-6 w-6"
              src="@/assets/png/icon_file_uploaded.png"
              alt="uploaded file icon"
            >
            <div>
              <p class="ml-6 text-gray-600">
                {{ selectedFile.name }}
              </p>
              <p class="ml-6 text-gray-500 text-sm">
                XML • {{ formatFileSize(selectedFile.size) }}
              </p>
            </div>
          </div>
          <button
            class="text-green-500 hover:text-green-700 transition-colors duration-300"
            @click="clearSelectedFile"
          >
            <img
              src="@/assets/svg/icon_48px_close.svg"
              alt="clear select file"
            >
          </button>
        </div>
      </div>
      <!--second setting -->
      <div v-if="!isEditing&&isSecondSetting&&!isMetadataFileUpload">
        <div class="p-6">
          <div class="flex justify-between items-start">
            <h3 class="mb-5">
              {{ $t('messages.entra_setup.staging_configuration_title') }}
            </h3>
            <div class="hidden sm:block md:block">
              <a
                class="cursor-pointer text-zyxel-green-700 font-bold ml-2"
                tabindex="0"
                role="button"
                @click="edit"
                @keydown.enter="edit"
                @keydown.space="edit"
              >
                {{ $t('messages.common.link_edit') }}
              </a>
            </div>
          </div>
          <div
            v-if="secondaryConfigState.isUnverified&&isVerifySummited"
            class="bg-amber-50 p-4 mb-4 rounded-sm"
          >
            <div class="flex items-center">
              <p class="text-zyxel-gray-800">
                {{ $t('messages.entra_setup.refresh_instruction') }}
                <button
                  class="text-zyxel-green-600 hover:text-zyxel-green-800 font-medium underline focus:outline-none"
                  @click="handleRefresh"
                >
                  {{ $t('messages.entra_setup.reload_button') }}
                </button>
              </p>
            </div>
          </div>
          <dl class="grid sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 sm:gap-y-5 flex items-center">
            <dt class="text-zyxel-gray-900 max-w-[240px] sm:mr-[120px]">
              {{ $t('messages.entra_setup.status_label') }}
            </dt>
            <dd :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}">
              <div
                class="float-left"
                :class="stateColor[secondaryConfigState.stateIndex]"
              />
              {{ secondEntraSetting.state }}
            </dd>
            <dt class="text-zyxel-gray-900 max-w-[240px] mt-3 sm:mt-0 sm:mr-[120px]">
              {{ $t('messages.entra_setup.app_id_label') }}
            </dt>
            <dd :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}">
              {{ secondaryConfigState.entityId }}
            </dd>
            <dt
              v-if="secondaryConfigState.isVerified"
              class="text-zyxel-gray-800 max-w-[240px] mt-3 sm:mt-0 sm:mr-[90px]"
            >
              <label for="secondaryDeploymentCode">{{ $t('messages.entra_setup.deployment_code_label') }}</label>
            </dt>
            <dd
              v-if="secondaryConfigState.isVerified"
              :class="{'text-ellipsis': true}"
            >
              <input
                id="secondaryDeploymentCode"
                v-model="deployment_code"
                :class="['text-input p-4 text-left h-12 w-full']"
                type="text"
              >
              <div class="flex items-center gap-2 text-zyxel-gray-800">
                <span>{{ $t('messages.entra_setup.miss_deployment_code_label') }}</span>
                <button
                  :disabled="isDisabled"
                  class="transition-colors duration-200 focus:outline-none"
                  :class="isDisabled ? 'text-zyxel-gray-400 cursor-not-allowed' : 'text-zyxel-green-600 hover:text-zyxel-green-700'"
                  @click="handleRegenerate"
                >
                  {{ $t('messages.entra_setup.regenerate_label') }}{{ countdownText }}
                </button>
              </div>
            </dd>
          </dl>
          <div class="mt-5 modal-footer hidden sm:flex justify-end">
            <button
              v-if="secondaryConfigState.isUnverified"
              class="mt-5 button-primary min-w-[120px]"
              @click="verify_configurtion(secondEntraSetting)"
            >
              {{ $t('messages.button.verify') }}
            </button>
            <button
              v-if="secondaryConfigState.isVerified"
              class="mt-5 button-primary min-w-[120px]"
              @click="deploy_configurtion"
            >
              {{ $t('messages.button.deploy') }}
            </button>
          </div>
          <div class="modal-footer flex-col sm:hidden p-6">
            <button
              v-if="secondaryConfigState.isUnverified"
              class="button-primary w-full mb-4"
              @click="verify_configurtion(secondEntraSetting)"
            >
              {{ $t('messages.button.verify') }}
            </button>
            <button
              v-if="secondaryConfigState.isVerified"
              class="button-primary w-full mb-4"
              @click="deploy_configurtion"
            >
              {{ $t('messages.button.deploy') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Loading
    v-model:active="isLoading"
    color="#3C9F00"
    loader="spinner"
    background-color="#FFFFFF"
    :is-full-page="true"
  />
  <GeneralFailDialog
    :is-show="isGeneralFailDialogShow"
    :title="generalFailDialogProps.title"
    :description="generalFailDialogProps.description"
    @close="closeGeneralFailDialog"
  />
</template>

<script setup>
import { reactive, ref, onMounted, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import { apiBaseUrl } from '@/config';
import axios from 'axios';
import GeneralFailDialog from "@/components/GeneralFailDialog.vue";
import { logger } from '@/utils/logger';

const { t } = useI18n();

// Reactive state
const isLoading = ref(false);
const entraOption = ref(false);
const isMetadataFileUpload = ref(true);
const domain = ref("");
const entraSettings = ref([]);
// Removed unused variable: const hasSecondConfig = ref(false);
const isEditing = ref(false);
const isSecondSetting = ref(false);
const deployment_code = ref('');
const isGeneralFailDialogShow = ref(false);
const firstEntraSetting = ref({});
const secondEntraSetting = ref({});
const isDisabled = ref(false);
const countdown = ref(30);
const stateColor = ref(["red", "orange", "green"]);
const isVerifySummited = ref(false);
const primaryConfigState = ref({
  setting: null,
  isUnverified: false,
  isVerified: false,
  isWorking: false,
  stateIndex: 0,
  entityId: ""
});
const secondaryConfigState = ref({
  setting: null,
  isUnverified: false,
  isVerified: false,
  isWorking: false,
  stateIndex: 0,
  entityId: ""
});
const ENTITY_ID_REGEX = new RegExp('/([^/]+)/$');

const generalFailDialogProps = reactive({
  title: "",
  description: ""
});

// File upload related refs
const isDragging = ref(false);
const selectedFile = ref(null);
const fileInput = ref(null);

// Fetch data on component creation
onMounted(async () => {
  await fetchSettings();
});

// 創建一個函數來處理 Entra 設定的狀態邏輯
const processEntraSetting = (setting) => {
  const result = {
    setting,
    isUnverified: setting.state === 'Unverified',
    isVerified: setting.state === 'Verified',
    isWorking: setting.state === 'Working',
    stateIndex: 0 // 預設為紅色
  };

  if (result.isUnverified) {
    result.stateIndex = 0;
  } else if (result.isVerified) {
    result.stateIndex = 1;
  } else if (result.isWorking) {
    result.stateIndex = 2;
  }

  return result;
};

// Methods
const fetchSettings = async () => {
  const authStore = useAuthStore();
  try {
    isLoading.value = true;
    entraSettings.value = JSON.parse(await authStore.getEntraSetting());
    if (entraSettings.value) {
      entraOption.value = true;
      if (entraSettings.value.length === 1) {
        isMetadataFileUpload.value = false;
        domain.value = entraSettings.value[0].domain;
        firstEntraSetting.value = entraSettings.value[0];
      } else if (entraSettings.value.length > 1) {
        isMetadataFileUpload.value = false;
        isSecondSetting.value = true;
        logger.log('Multiple settings found');

        const workingSetting = entraSettings.value.find(item => item.state === 'Working');
        const otherSetting = entraSettings.value.find(item => item !== workingSetting);

        if (workingSetting) {
          firstEntraSetting.value = workingSetting;
          secondEntraSetting.value = otherSetting;
        } else {
          // If there is no 'Working', put them in the original order
          firstEntraSetting.value = entraSettings.value[0];
          secondEntraSetting.value = entraSettings.value[1];
        }
        domain.value = firstEntraSetting.value.domain;

        const result = processEntraSetting(secondEntraSetting.value);
        if (secondEntraSetting.value && secondEntraSetting.value.entity_id) {
          const secondMatchResult = secondEntraSetting.value.entity_id.match(ENTITY_ID_REGEX);
          const secondaryEntityId = secondMatchResult ? secondMatchResult[1] : '';
          secondaryConfigState.value = { ...result, entityId: secondaryEntityId };
        }
      }
      // 使用重構後的函數處理狀態
      const result = processEntraSetting(firstEntraSetting.value);
      if (firstEntraSetting.value && firstEntraSetting.value.entity_id) {
        const matchResult = firstEntraSetting.value.entity_id.match(ENTITY_ID_REGEX);
        const primaryEntityId = matchResult ? matchResult[1] : '';
        primaryConfigState.value = { ...result, entityId: primaryEntityId };
      }
    } else {
      domain.value = await authStore.getDomainByEmail();
    }
    logger.log('entraOption:', entraOption.value);
    logger.log('domain :', domain.value);
  } catch (error) {
    logger.error('Error fetching entra setting:', error);
  } finally {
    isLoading.value = false;
  }
};

const goToUrl = (url) => {
  window.open(url, '_blank');
};

const downloadMetadata = () => {
  const apiUrl = `${apiBaseUrl}/api/entra_id/sp-metadata/${domain.value}`;
  fetch(apiUrl)
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zyxel_account_metadata.xml';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    })
    .catch(error => {
      logger.error('下載 metadata 失敗:', error);
    });
};

const secondSetting = () => {
  isSecondSetting.value = true;
  isMetadataFileUpload.value = true;
  selectedFile.value = null;
}

// File handling methods
const onDragOver = () => {
  isDragging.value = true;
};

const onDragLeave = () => {
  isDragging.value = false;
};

const onDrop = (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0 && files[0].type === 'text/xml') {
    selectedFile.value = files[0];
  } else {
    showFailDialogByType('metadataUploadFail');
  }
};

const onFileChange = (event) => {
  const files = event.target.files;
  if (files.length > 0 && files[0].type === 'text/xml') {
    selectedFile.value = files[0];
  } else {
    showFailDialogByType('metadataUploadFail');
  }
};

const handleFileUpload = async (file) => {
  logger.log('Uploading file:', file);

  const formData = new FormData();
  formData.append('uploadFile', file);

  try {
    await axios.post(
      `${apiBaseUrl}/api/entra_id/metadata_upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    isEditing.value = false;
    await fetchSettings();
  } catch (error) {
    logger.error('Upload failed:', error);
    showFailDialogByType('metadataUploadFail');
  }
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + 'B';
  const kb = bytes / 1024;
  return kb.toFixed(0) + 'KB';
};

const clearSelectedFile = () => {
  selectedFile.value = null;
};

const triggerFileInput = () => {
  fileInput.value.click();
};

const cancelEdit = async () => {
  selectedFile.value = null;
  isEditing.value = false;
  isSecondSetting.value = false;
  await fetchSettings();
};

const edit = () => {
  logger.log("edit");
  isMetadataFileUpload.value = true;
  selectedFile.value = null;
  isEditing.value = true;
};

const handleRefresh = async () => {
  await fetchSettings();
};

const verify_configurtion = async (setting) => {
  try {
    isVerifySummited.value = true;
    const response = await axios.post(`${apiBaseUrl}/api/entra_id/verify_config`, {
      domain: setting.domain,
      entity_id: setting.entity_id
    });

    if (response.status === 200) {
      // 讓瀏覽器跳轉到這個 URL
      window.open(response.data.url, '_blank');
    } else {
      logger.log("驗證失敗！");
    }
  } catch (error) {
    logger.log("entra id verify error: ", error);
    if (error.response) {
      const detail = error.response.data?.detail;
      if (detail === "NO_MATCHING_UNVERIFIED_CONFIG") {
        showFailDialogByType('noMatchUnverifiedConfigFail');
      } else if (detail === "ENTRA_CONFIG_NOT_FOUND") {
        showFailDialogByType('entraConfigNotFoundFail');
      } else if (detail === "MORE_THAN_ONE_UNVERIFIED") {
        showFailDialogByType('moreThanOneUnverifiedFail');
      } else if (detail === "INVALID_ENTITY_ID") {
        showFailDialogByType('invalidEntityIdFail');
      } else if (detail === "MISSING_ENTITY_ID") {
        showFailDialogByType('missingEntityIdFail');
      }
    }
  }
};

const deploy_configurtion = async () => {
  try {
    isLoading.value = true;
    if (deployment_code.value.trim().length === 0) {
      showFailDialogByType('deployCodeFail');
      isLoading.value = false;
      return;
    }
    const response = await axios.post(`${apiBaseUrl}/api/entra_id/deploy`, {
      deploy_code: deployment_code.value,
    });

    const isSuccess = response.status === 200 && response.data.success === true;
    if (!isSuccess) {
      logger.log("isSuccess");
      isLoading.value = false;
      return;
    }
    isSecondSetting.value = false;
    await fetchSettings();
  } catch (error) {
    logger.log("entra id verify error: ", error);
    if (error.response) {
      const detail = error.response.data?.detail;
      if (detail === "ENTRA_CONFIG_NOT_FOUND") {
        showFailDialogByType('entraConfigNotFoundFail');
      } else if (detail === "STATE_NOT_VERIFIED") {
        showFailDialogByType('stateNotVerifiedFail');
      } else if (detail === "DEPLOY_CODE_MISMATCH") {
        showFailDialogByType('deployCodeMismatchFail');
      } else if (detail === "DEPLOY_CODE_EXPIRED") {
        showFailDialogByType('deployCodeExpiredFail');
      } else if (detail === "CONFIG_DATA_INCOMPLETE") {
        showFailDialogByType('configDataImcompleteFail');
      } else {
        showFailDialogByType('deploymentProcessFailedFail');
      }
    }
  } finally {
    isLoading.value = false;
  }
};

const ERROR_DIALOG_MAP = {
  deployCodeFail: () => ({
    title: t('messages.entra_id_deployment_code_fail_modal.title'),
    description: t('messages.entra_id_deployment_code_fail_modal.description')
  }),
  metadataUploadFail: () => ({
    title: t('messages.entra_id_metadata_upload_fail_modal.title'),
    description: t('messages.entra_id_metadata_upload_fail_modal.description')
  }),
  noMatchUnverifiedConfigFail: () => ({
    title: t('messages.dialog.no_matching_unverified_configuration.title'),
    description: t('messages.dialog.no_matching_unverified_configuration.description')
  }),
  entraConfigNotFoundFail: () => ({
    title: t('messages.dialog.entra_config_not_found.title'),
    description: t('messages.dialog.entra_config_not_found.description')
  }),
  moreThanOneUnverifiedFail: () => ({
    title: t('messages.dialog.more_than_one_unverified.title'),
    description: t('messages.dialog.more_than_one_unverified.description')
  }),
  invalidEntityIdFail: () => ({
    title: t('messages.dialog.invalid_entity_id.title'),
    description: t('messages.dialog.invalid_entity_id.description')
  }),
  missingEntityIdFail: () => ({
    title: t('messages.dialog.missing_entity_id.title'),
    description: t('messages.dialog.missing_entity_id.description')
  }),
  stateNotVerifiedFail: () => ({
    title: t('messages.dialog.state_not_verified.title'),
    description: t('messages.dialog.state_not_verified.description')
  }),
  deployCodeMismatchFail: () => ({
    title: t('messages.dialog.deploy_code_mismatch.title'),
    description: t('messages.dialog.deploy_code_mismatch.description')
  }),
  deployCodeExpiredFail: () => ({
    title: t('messages.dialog.deploy_code_expired.title'),
    description: t('messages.dialog.deploy_code_expired.description')
  }),
  configDataImcompleteFail: () => ({
    title: t('messages.dialog.config_data_incomplete.title'),
    description: t('messages.dialog.config_data_incomplete.description')
  }),
  deploymentProcessFailedFail: () => ({
    title: t('messages.dialog.deployment_process_failed.title'),
    description: t('messages.dialog.deployment_process_failed.description')
  })
};

const showFailDialogByType = (type) => {
  const getConfig = ERROR_DIALOG_MAP[type];
  if (getConfig) {
    const { title, description } = getConfig(); // 延遲執行取得翻譯文字
    showFailDialog(title, description);
  }
};

const showFailDialog = (title, description) => {
  generalFailDialogProps.title = title;
  generalFailDialogProps.description = description;
  isGeneralFailDialogShow.value = true;
};

const closeGeneralFailDialog = () => {
  isGeneralFailDialogShow.value = false;
};

const countdownText = computed(() => {
  return isDisabled.value ? ` (${countdown.value}s)` : '';
});

let timer = null;

const handleRegenerate = async () => {
  try {
    const response = await axios.get(`${apiBaseUrl}/api/entra_id/deployment_code/regen`);

    if (response.status === 200) {
      // 讓瀏覽器跳轉到這個 URL
      // Open a new tab
      window.open(response.data.url, '_blank');
    } else {
      logger.log("Regenerate 失敗！");
    }
  } catch (error) {
    logger.log("Regenerate error: ", error);
  }

  // Disable the button and start countdown
  isDisabled.value = true;
  countdown.value = 30;

  // Start the countdown timer
  timer = setInterval(() => {
    countdown.value--;

    if (countdown.value <= 0) {
      clearInterval(timer);
      isDisabled.value = false;
    }
  }, 1000);
};

const toggleClasses = computed(() => {
  const baseClasses = 'w-9 h-5 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-zyxel-green-300 dark:peer-focus:ring-zyxel-green-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[\'\'] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 outline-none';
  return {
    [baseClasses]: true,
    'bg-zyxel-green-500': primaryConfigState.value.isWorking,
    'bg-gray-200 peer-checked:bg-zyxel-green-500': !primaryConfigState.value.isWorking
  };
});

// Helper function for sleep
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Watch entraOption for changes
watch(entraOption, async (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    // when close entraOption must clear selectedFile
    selectedFile.value = null;
    if(isMetadataFileUpload.value === false) {
      try {
        isLoading.value = true;
        const response = await axios.post(`${apiBaseUrl}/api/entra_id/delete_config`);
        if (response.status === 200) {
          isMetadataFileUpload.value = true;
          domain.value = "";
          entraSettings.value = [];
          isEditing.value = false;
          isSecondSetting.value = false;
        } else {
          logger.error('delete_config API call failed:', response.status);
          showFailDialogByType("configDataImcompleteFail");
        }
      } catch (error) {
        logger.error('Error calling delete_config API:', error);
        showFailDialogByType("configDataImcompleteFail");
      } finally {
        await sleep(1000);
        isLoading.value = false;
      }
    }
  } else {
    await fetchSettings();
  }
});
</script>

<style scoped>
.green{
    width:15px;
    height:15px;
    background:#64BE00;
    border:1px solid #64BE00;
    border-radius:50%;
    margin-top: 4px;
    margin-right: 12px;
}
.red{
    width:15px;
    height:15px;
    background:#FF2837;
    border:1px solid #FF2837;
    border-radius:50%;
    margin-top: 4px;
    margin-right: 12px;
}
.orange{
    width:15px;
    height:15px;
    background:#FFA133;
    border:1px solid #FFA133;
    border-radius:50%;
    margin-top: 4px;
    margin-right: 12px;
}
</style>